import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Plus, Edit2, Trash2, ToggleLeft, ToggleRight, X, Search, Filter } from 'lucide-react';
import '../styles/Admin.css';
import { 
    getAllFormFields, 
    createFormField, 
    updateFormField, 
    deleteFormField, 
    toggleFormFieldStatus 
} from '../ServiceAPI/AdminUser/formfieldapi';
import { getAllService } from '../ServiceAPI/services/api';

const FormFieldManagement = () => {
    const navigate = useNavigate();
    const [formFields, setFormFields] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [inputTypeFilter, setInputTypeFilter] = useState("all");
    const [requiredFilter, setRequiredFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    let counter = 0;
    const [formData, setFormData] = useState({
        Service_id: '',
        Field_label: '',
        Input_type: 'text',
        Is_required: 0,
        Options: '',
        Status: 1,
        Priority: 0
    });

    const inputTypes = [
        'text', 'email', 'password', 'number', 'tel', 'date', 'datetime-local',
        'time', 'url', 'search', 'color', 'range', 'textarea', 'select', 
        'checkbox', 'radio', 'file', 'hidden'
    ];

    // Check authentication
    useEffect(() => {
        const isAdminAuthenticated = localStorage.getItem("adminAuth") === "true";
        if (!isAdminAuthenticated) {
            navigate("/admin/login");
            return;
        }
    }, [navigate]);

    // Fetch form fields and services
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const fieldsData = await getAllFormFields();
                
                setFormFields(Array.isArray(fieldsData) ? fieldsData : fieldsData.data || []);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                ...formData,
                Service_id: parseInt(formData.Service_id),
                Is_required: parseInt(formData.Is_required),
                Status: parseInt(formData.Status),
                Priority: parseInt(formData.Priority || 0, 10)
            };

            let savedField = null;
            if (editingField) {
                const response = await updateFormField(editingField.Id, submitData);
                savedField = response?.data || null;
            } else {
                const response = await createFormField(submitData);
                savedField = response?.data || null;
            }

            // Refresh data using a fresh response
            const fieldsData = await getAllFormFields();
            const nextFields = Array.isArray(fieldsData) ? fieldsData : fieldsData.data || [];
            setFormFields(nextFields);

            if (savedField?.Id) {
                const fieldWithPriority = {
                    ...savedField,
                    Priority: Number(savedField.Priority ?? submitData.Priority ?? 0)
                };
                setFormFields(prevFields => {
                    const exists = prevFields.some(field => field.Id === fieldWithPriority.Id);
                    if (exists) {
                        return prevFields.map(field => field.Id === fieldWithPriority.Id ? fieldWithPriority : field);
                    }
                    return [fieldWithPriority, ...prevFields];
                });
            }
            
            // Reset form
            setFormData({
                Service_id: '',
                Field_label: '',
                Input_type: 'text',
                Is_required: 0,
                Options: '',
                Status: 1,
                Priority: 0
            });
            setEditingField(null);
            setShowModal(false);
        } catch (err) {
            console.error('Error saving form field:', err);
            setError('Failed to save form field');
        }
    };

    const handleEdit = async (field) => {
        setEditingField(field);
        setFormData({
            Service_id: field.Service_id,
            Field_label: field.Field_label,
            Input_type: field.Input_type,
            Is_required: field.Is_required,
            Options: field.Options || '',
            Status: field.Status,
            Priority: field.Priority || 0
        });
        
        // Fetch services when modal opens for editing
        try {
            
            const servicesData = await getAllService();
            
            
            let servicesArray = [];
            if (Array.isArray(servicesData)) {
                servicesArray = servicesData;
            } else if (servicesData.data && Array.isArray(servicesData.data)) {
                servicesArray = servicesData.data;
            } else if (servicesData.services && Array.isArray(servicesData.services)) {
                servicesArray = servicesData.services;
            }
            
            
            setServices(servicesArray);
        } catch (err) {
            console.error('Error fetching services:', err);
            setError('Failed to load services');
        }
        
        setShowModal(true);
    };

    const handleDelete = async (fieldId) => {
        if (window.confirm('Are you sure you want to delete this form field?')) {
            try {
                await deleteFormField(fieldId);
                const fieldsData = await getAllFormFields();
                setFormFields(Array.isArray(fieldsData) ? fieldsData : fieldsData.data || []);
            } catch (err) {
                console.error('Error deleting form field:', err);
                setError('Failed to delete form field');
            }
        }
    };

    const handleToggleStatus = async (fieldId) => {
        try {
            await toggleFormFieldStatus(fieldId);
            const fieldsData = await getAllFormFields();
            setFormFields(Array.isArray(fieldsData) ? fieldsData : fieldsData.data || []);
        } catch (err) {
            console.error('Error toggling status:', err);
            setError('Failed to toggle status');
        }
    };

    const openCreateModal = async () => {
        setEditingField(null);
        setFormData({
            Service_id: '',
            Field_label: '',
            Input_type: 'text',
            Is_required: 0,
            Options: '',
            Status: 1,
            Priority: 0
        });
        
        // Fetch services when modal opens
        try {
            
            const servicesData = await getAllService();
            
            
            let servicesArray = [];
            if (Array.isArray(servicesData)) {
                servicesArray = servicesData;
            } else if (servicesData.data && Array.isArray(servicesData.data)) {
                servicesArray = servicesData.data;
            } else if (servicesData.services && Array.isArray(servicesData.services)) {
                servicesArray = servicesData.services;
            }
            
            
            setServices(servicesArray);
        } catch (err) {
            console.error('Error fetching services:', err);
            setError('Failed to load services');
        }
        
        setShowModal(true);
    };

    // Filter form fields based on search and filters
    const filteredFormFields = formFields.filter(field => {
        // Search filter
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
            !searchTerm ||
            (field.ServiceName && field.ServiceName.toLowerCase().includes(searchLower)) ||
            (field.ServiceType && field.ServiceType.toLowerCase().includes(searchLower)) ||
            (field.ServiceCategory && field.ServiceCategory.toLowerCase().includes(searchLower)) ||
            (field.Field_label && field.Field_label.toLowerCase().includes(searchLower));

        // Input type filter
        const matchesInputType = 
            inputTypeFilter === 'all' || 
            field.Input_type === inputTypeFilter;

        // Required filter
        const matchesRequired = 
            requiredFilter === 'all' ||
            (requiredFilter === 'required' && field.Is_required === 1) ||
            (requiredFilter === 'optional' && field.Is_required === 0);

        // Status filter
        const matchesStatus = 
            statusFilter === 'all' ||
            (statusFilter === 'active' && field.Status === 1) ||
            (statusFilter === 'inactive' && field.Status === 0);

        return matchesSearch && matchesInputType && matchesRequired && matchesStatus;
    });

    if (loading) return (
        <AdminLayout>
            <div className="loading-container">
                <div className="spinner-large"></div>
                <p>Loading form fields...</p>
            </div>
        </AdminLayout>
    );

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Form Field Management</h1>
                    <p className="admin-page-subtitle">Configure dynamic form fields for services</p>
                </div>
                <button 
                    className="btn-primary-modern"
                    onClick={openCreateModal}
                >
                    <Plus size={20} />
                    Add New Field
                </button>
            </div>

            {error && (
                <div className="alert alert-error">
                    <span>{error}</span>
                    <button onClick={() => setError('')} className="alert-close">
                        <X size={16} />
                    </button>
                </div>
            )}

            <div className="admin-content-area">
                <div className="data-table-card">
                    <div className="data-table-header">
                        <h3>Form Fields ({filteredFormFields.length} of {formFields.length})</h3>
                    </div>
                    
                    {/* Search and Filter Section */}
                    <div className="search-filter-section">
                        <div className="search-box">
                            <Search size={20} />
                            <input
                                type="text"
                                placeholder="Search by Service, Field Label..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <div className="filter-group">
                            <Filter size={18} />
                            <select
                                value={inputTypeFilter}
                                onChange={(e) => setInputTypeFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Types</option>
                                {inputTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                            <select
                                value={requiredFilter}
                                onChange={(e) => setRequiredFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Fields</option>
                                <option value="required">Required</option>
                                <option value="optional">Optional</option>
                            </select>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table className="modern-table">
                            <thead>
                                <tr>
                                    <th>Sl No.</th>
                                    <th>Service</th>
                                    <th>Field Label</th>
                                    <th>Input Type</th>
                                    <th>Required</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredFormFields.length > 0 ? (
                                    filteredFormFields.map(field => (
                                        <tr key={field.Id}>
                                            <td><span className="td-badge">{++counter}</span></td>
                                            <td>
                                                <div className="service-info-cell">
                                                    <strong>{field.ServiceName}</strong>
                                                    <small>{field.ServiceType} | {field.ServiceCategory}</small>
                                                </div>
                                            </td>
                                            <td><strong>{field.Field_label}</strong></td>
                                            <td>
                                                <span className="type-badge">
                                                    {field.Input_type}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`status-badge-modern ${field.Is_required ? 'required' : 'optional'}`}>
                                                    {field.Is_required ? 'Required' : 'Optional'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`status-badge-modern ${field.Status ? 'active' : 'inactive'}`}>
                                                    {field.Status ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="table-action-group">
                                                    <button
                                                        className="action-btn primary"
                                                        onClick={() => handleEdit(field)}
                                                        title="Edit"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        className={`action-btn ${field.Status ? 'warning' : 'success'}`}
                                                        onClick={() => handleToggleStatus(field.Id)}
                                                        title={field.Status ? 'Deactivate' : 'Activate'}
                                                    >
                                                        {field.Status ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                                                    </button>
                                                    <button
                                                        className="action-btn danger"
                                                        onClick={() => handleDelete(field.Id)}
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="empty-cell">
                                            <div className="empty-state">
                                                <Plus size={48} />
                                                <p>No form fields found</p>
                                                <button className="btn-secondary-modern" onClick={openCreateModal}>
                                                    Create your first form field
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay-modern">
                    <div className="modal-container-modern">
                        <div className="modal-header-modern">
                            <h3>{editingField ? 'Edit Form Field' : 'Add New Form Field'}</h3>
                            <button 
                                className="modal-close-btn"
                                onClick={() => setShowModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="modal-form-modern">
                            <div className="form-group-modern">
                                <label>Service *</label>
                                <select
                                    name="Service_id"
                                    value={formData.Service_id}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Service</option>
                                    {services.length > 0 ? (
                                        services.map(service => (
                                            <option key={service.Id} value={service.Id}>
                                                {service.Name} - {service.Type} - {service.Category}
                                            </option>
                                        ))
                                    ) : (
                                        <option disabled>No services available</option>
                                    )}
                                </select>
                            </div>

                            <div className="form-row">
                                <div className="form-group-modern">
                                    <label>Field Label *</label>
                                    <input
                                        type="text"
                                        name="Field_label"
                                        value={formData.Field_label}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Applicant Name, Date of Birth"
                                        required
                                    />
                                </div>

                                <div className="form-group-modern">
                                    <label>Input Type *</label>
                                    <select
                                        name="Input_type"
                                        value={formData.Input_type}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        {inputTypes.map(type => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group-modern">
                                <label>Display Priority</label>
                                <input
                                    type="number"
                                    name="Priority"
                                    value={formData.Priority}
                                    onChange={handleInputChange}
                                    placeholder="0"
                                    min="0"
                                />
                                <small className="form-hint">Lower numbers appear first.</small>
                            </div>

                            <div className="form-group-modern">
                                <label>Options (JSON format for select/radio)</label>
                                <textarea
                                    name="Options"
                                    value={formData.Options}
                                    onChange={handleInputChange}
                                    placeholder='{"option1": "Label 1", "option2": "Label 2"}'
                                    rows="3"
                                />
                                <small className="form-hint">Only required for select, radio, or checkbox types</small>
                            </div>

                            <div className="form-group-modern checkbox-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="Is_required"
                                        checked={formData.Is_required === 1}
                                        onChange={handleInputChange}
                                    />
                                    <span>Required Field</span>
                                </label>
                                
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="Status"
                                        checked={formData.Status === 1}
                                        onChange={handleInputChange}
                                    />
                                    <span>Active Status</span>
                                </label>
                            </div>

                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary-modern">
                                    {editingField ? 'Update Field' : 'Create Field'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default FormFieldManagement;